import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { ApiError } from '@/api/errors'
import type { TravelPlanResponse, TravelPlanWriteRequest } from '@/api/types'
import { Banner } from '@/components/Banner'
import { DateField } from '@/components/DateField'
import { TextField } from '@/components/TextField'
import { TimeField } from '@/components/TimeField'
import { useToast } from '@/components/Toast'
import { todayIso } from '@/lib/dates'
import { travel as api } from '@/features/callers/api'
import { errorMessage } from '@/api/messages'

function blank(): TravelPlanWriteRequest {
  const today = todayIso()
  return {
    travel_date: today,
    end_date: today,
    location: '',
    depart_at: null,
    return_at: null,
    notes: null,
  }
}

/** Adding a day to the travel plan. Small enough that the dialog holds the
 *  whole form without scrolling on any screen worth the name. */
export function useTravelDayForm({ onAdded }: { onAdded: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<TravelPlanWriteRequest>(blank)
  const [error, setError] = useState<string | null>(null)

  const add = useMutation<TravelPlanResponse, ApiError>({
    mutationFn: () => api.create(form),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['travel'] })
      await queryClient.invalidateQueries({ queryKey: ['calendar'] })
      toast.show('Travel day added')
      onAdded()
    },
    onError: (err) => setError(errorMessage(err)),
  })

  function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    if (!form.location.trim()) {
      setError('A location is required.')
      return
    }
    if ((form.end_date ?? form.travel_date) < form.travel_date) {
      setError('The last day cannot be before the first.')
      return
    }
    add.mutate()
  }

  return { form, setForm, error, add, submit }
}

export type TravelDayState = ReturnType<typeof useTravelDayForm>

export function TravelDayFields({ state }: { state: TravelDayState }) {
  const { form, setForm, error } = state
  return (
    <div className="grid gap-4">
      {error && <Banner tone="danger">{error}</Banner>}
      <TextField
        label="Location"
        required
        autoFocus
        placeholder="Coimbatore"
        value={form.location}
        onChange={(e) => setForm({ ...form, location: e.target.value })}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DateField
          label="First day"
          required
          value={form.travel_date}
          onChange={(iso) =>
            setForm((current) => ({
              ...current,
              travel_date: iso,
              // A trip that now ends before it starts is a slip, not an
              // instruction: drag the last day along rather than refusing.
              end_date:
                !current.end_date || current.end_date < iso ? iso : current.end_date,
            }))
          }
        />
        <DateField
          label="Last day"
          required
          min={form.travel_date}
          hint="Same day for a single visit."
          value={form.end_date ?? form.travel_date}
          onChange={(iso) => setForm((current) => ({ ...current, end_date: iso }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <TimeField
          label="Leaves"
          value={form.depart_at ?? ''}
          onChange={(time) => setForm({ ...form, depart_at: time || null })}
        />
        <TimeField
          label="Back"
          value={form.return_at ?? ''}
          onChange={(time) => setForm({ ...form, return_at: time || null })}
        />
      </div>
      <TextField
        label="Notes"
        value={form.notes ?? ''}
        onChange={(e) => setForm({ ...form, notes: e.target.value || null })}
      />
    </div>
  )
}
